import type { Article } from "../wikidaily/types";

export type OptimisticSelfState = {
  mode: "move" | "reset";
  currentTry: Article[];
  currentArticle: Article | undefined;
};

export function createOptimisticMove(
  basePath: Article[],
  article: Article,
): OptimisticSelfState {
  return {
    mode: "move",
    currentTry: appendArticleToPath(basePath, article),
    currentArticle: article,
  };
}

export function createOptimisticReset(
  startArticle: Article | undefined,
): OptimisticSelfState {
  return {
    mode: "reset",
    currentTry: startArticle ? [startArticle] : [],
    currentArticle: startArticle,
  };
}

export function appendArticleToPath(
  path: Article[],
  article: Article,
): Article[] {
  if (path.at(-1)?.id === article.id) return path;
  return [...path, article];
}

export function getUsableOptimisticSelfState(
  optimistic: OptimisticSelfState | null,
  serverCurrentTry: Article[],
): OptimisticSelfState | null {
  if (!optimistic) return null;

  if (optimistic.mode === "reset") {
    return areSameArticlePath(serverCurrentTry, optimistic.currentTry)
      ? null
      : optimistic;
  }

  if (areSameArticlePath(serverCurrentTry, optimistic.currentTry)) return null;

  return isArticlePathPrefix(serverCurrentTry, optimistic.currentTry)
    ? optimistic
    : null;
}

export function getDisplayedBestTry(
  serverBestTry: Article[] | null | undefined,
  optimisticBestTry: Article[] | null,
): Article[] {
  if (!optimisticBestTry) return serverBestTry ?? [];
  if (!serverBestTry) return optimisticBestTry;
  if (serverBestTry.length <= optimisticBestTry.length) return serverBestTry;
  return optimisticBestTry;
}

export function getImprovedBestTry(
  serverBestTry: Article[] | null | undefined,
  candidateBestTry: Article[],
): Article[] | null {
  if (candidateBestTry.length === 0) return null;
  if (serverBestTry && serverBestTry.length <= candidateBestTry.length)
    return null;
  return candidateBestTry;
}

export function shouldKeepOptimisticBestTry(
  optimisticBestTry: Article[],
  serverBestTry: Article[] | null | undefined,
): boolean {
  if (!serverBestTry) return true;
  if (areSameArticlePath(serverBestTry, optimisticBestTry)) return false;
  return serverBestTry.length > optimisticBestTry.length;
}

function isArticlePathPrefix(prefix: Article[], path: Article[]): boolean {
  if (prefix.length > path.length) return false;

  return prefix.every((article, index) => article.id === path[index]?.id);
}

function areSameArticlePath(left: Article[], right: Article[]): boolean {
  return left.length === right.length && isArticlePathPrefix(left, right);
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest;

  const article = (id: number): Article => ({
    id,
    title: `Article ${id}`,
    description: null,
  });

  describe("duels optimistic helpers", () => {
    it("keeps an optimistic move while the server path is still a prefix", () => {
      const optimistic = createOptimisticMove([article(1)], article(2));

      expect(getUsableOptimisticSelfState(optimistic, [article(1)])).toEqual(
        optimistic,
      );
    });

    it("drops an optimistic move once the server path catches up", () => {
      const optimistic = createOptimisticMove([article(1)], article(2));

      expect(
        getUsableOptimisticSelfState(optimistic, [article(1), article(2)]),
      ).toBeNull();
    });

    it("uses reset optimism while the server still reports the old path", () => {
      const optimistic = createOptimisticReset(article(10));

      expect(optimistic.currentTry).toEqual([article(10)]);
      expect(
        getUsableOptimisticSelfState(optimistic, [article(1), article(2)]),
      ).toEqual(optimistic);
    });

    it("drops reset optimism once the server confirms the start-only path", () => {
      const optimistic = createOptimisticReset(article(10));

      expect(
        getUsableOptimisticSelfState(optimistic, [article(10)]),
      ).toBeNull();
    });

    it("only displays an optimistic best path when it improves the server best", () => {
      expect(
        getDisplayedBestTry([article(1), article(2)], [article(1)]),
      ).toEqual([article(1)]);
      expect(
        getDisplayedBestTry([article(1)], [article(1), article(2)]),
      ).toEqual([article(1)]);
    });
  });
}
